<template>
  <teleport to="body">
    <div class="fixed inset-0 z-[2147483647] overflow-hidden bg-slate-950/70 backdrop-blur-sm">
      <div class="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div
          ref="shellRef"
          :dir="isRtl === true ? 'rtl' : 'ltr'"
          class="terms-modal-shell relative flex w-full max-h-[88vh] max-w-5xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white text-slate-900 shadow-[0_32px_90px_rgba(15,23,42,0.34)] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          <div class="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(148,163,184,0.18),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_32%)] dark:bg-[radial-gradient(circle_at_top,rgba(51,65,85,0.45),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.12),transparent_28%)]"></div>
            <div class="absolute inset-0 flex flex-wrap content-start overflow-hidden opacity-90" :style="{ width: shellSize.width + 'px', height: shellSize.height + 'px' }">
              <div
                v-for="(pixelTone, index) in pixelStates"
                :key="index"
                class="h-[40px] w-[40px] border-b border-r border-slate-200/50 transition-colors duration-1000 dark:border-slate-800/60"
                :class="getPixelClass(pixelTone)"
              ></div>
            </div>
          </div>
          <div class="terms-modal-header relative z-10 shrink-0 border-b border-slate-200/80 px-6 py-5 dark:border-slate-700/80 sm:px-8">
            <div class="w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
              <h2 class="w-full text-2xl font-semibold tracking-tight sm:text-[32px]">{{ titleText }}</h2>
              <div class="mt-2 text-sm text-slate-500 dark:text-slate-400">{{ versionText }}</div>
            </div>
          </div>
          <div class="terms-modal-scroll relative z-10 min-h-0 flex-1 overflow-y-scroll bg-slate-50/80 px-4 py-4 dark:bg-slate-950/60 sm:px-6 sm:py-6">
            <div
              class="rounded-[24px] border border-slate-200/80 bg-white px-5 py-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:px-8 sm:py-8"
            >
              <div class="policy-content prose prose-sm max-w-none dark:prose-invert" :class="isRtl === true ? 'text-right' : 'text-left'" v-html="html"></div>
            </div>
          </div>
          <div class="relative z-10 shrink-0 border-t border-slate-200/80 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-700/80 dark:bg-slate-900/95 sm:px-6 sm:py-5">
            <div class="flex items-end justify-between max-sm:flex-col max-sm:items-stretch" :class="isRtl === true ? 'sm:flex-row-reverse' : ''">
              <label
                class="flex max-w-2xl items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-200"
                :class="isRtl === true ? 'flex-row-reverse text-right' : ''"
              >
                <input
                  v-model="confirmed"
                  type="checkbox"
                  class="mt-0.5 h-6 w-6 rounded-md border-slate-300 accent-primary dark:border-slate-600"
                />
                <span class="pt-0.5 text-[15px]" :class="isRtl === true ? 'mr-3' : 'ml-3'">{{ acceptCheckboxText }}</span>
              </label>
              <div class="flex items-center max-sm:mt-4" :class="isRtl === true ? 'sm:flex-row-reverse' : ''">
                <button class="btn min-w-[132px] rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700" @click="logout">
                  {{ logoutText }}
                </button>
                <button
                  :disabled="confirmed === false"
                  class="btn min-w-[180px] rounded-xl px-5 py-2.5 text-sm font-semibold shadow-sm"
                  :class="confirmed === true ? (isRtl === true ? 'mr-3' : 'ml-3') : (isRtl === true ? 'mr-3 opacity-50 cursor-not-allowed' : 'ml-3 opacity-50 cursor-not-allowed')"
                  @click="accept"
                >
                  {{ acceptButtonText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>
<script setup>
import { globalState } from '@/globalState';
import axios from '@/api/axiosInstance';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from '@/i18n';
import { marked } from 'marked';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';
import { useToast } from 'vue-toastification';
const emit = defineEmits(['accepted','logout']);
const confirmed = ref(false);
const version = '2025-08-28';
const shellRef = ref(null);
const shellSize = ref({ width: 0, height: 0 });
const pixelStates = ref([]);
const pixelToneClasses = [
  'bg-transparent',
  'bg-slate-200/70 dark:bg-slate-800/72',
  'bg-slate-300/55 dark:bg-slate-700/62',
  'bg-blue-100/65 dark:bg-slate-700/52',
  'bg-indigo-100/50 dark:bg-slate-800/60'
];
let pixelInterval = null;
let shellObserver = null;
const { currentLanguage } = useI18n();
const toast = useToast();
const titleText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'תנאי שימוש'; }
  if (lang === 'ar') { return 'شروط الاستخدام'; }
  if (lang === 'es') { return 'Términos de uso'; }
  if (lang === 'it') { return 'Termini di utilizzo'; }
  return 'Terms of Use';
});
const versionText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'גרסה: ' + version + ' • עודכן לאחרונה: 28 אוג׳ 2025'; }
  if (lang === 'ar') { return 'الإصدار: ' + version + ' • آخر تحديث: 28 آب 2025'; }
  if (lang === 'es') { return 'Versión: ' + version + ' • Última actualización: 28 Ago 2025'; }
  if (lang === 'it') { return 'Versione: ' + version + ' • Ultimo aggiornamento: 28 Ago 2025'; }
  return 'Version: ' + version + ' • Last updated: 28 Aug 2025';
});
const acceptCheckboxText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'קראתי ואני מאשר בשם הארגון שלי'; }
  if (lang === 'ar') { return 'قرأت وأوافق نيابةً عن مؤسستي'; }
  if (lang === 'es') { return 'He leído y acepto en nombre de mi organización'; }
  if (lang === 'it') { return 'Ho letto e accetto per conto della mia organizzazione'; }
  return 'I have read and I accept on behalf of my organization';
});
const logoutText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'התנתק'; }
  if (lang === 'ar') { return 'تسجيل الخروج'; }
  if (lang === 'es') { return 'Cerrar sesión'; }
  if (lang === 'it') { return 'Esci'; }
  return 'Logout';
});
const acceptButtonText = computed(() => {
  const lang = getPreferredLanguageShort();
  if (lang === 'he') { return 'קבל והמשך'; }
  if (lang === 'ar') { return 'قبول والمتابعة'; }
  if (lang === 'es') { return 'Aceptar y continuar'; }
  if (lang === 'it') { return 'Accetta e continua'; }
  return 'Accept and continue';
});
const isRtl = computed(() => {
  try {
    const fromI18n = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
    if (fromI18n === 'he' || fromI18n === 'ar' || (typeof fromI18n === 'string' && (fromI18n.startsWith('he') === true || fromI18n.startsWith('ar') === true))) {
      return true;
    }
    const fromGlobal = (globalState?.orgLanguage !== undefined ? globalState.orgLanguage : '').toString().toLowerCase();
    if (fromGlobal === 'he' || fromGlobal === 'ar' || fromGlobal.startsWith('he') === true || fromGlobal.startsWith('ar') === true) {
      return true;
    }
    const htmlLang = (document?.documentElement?.lang !== undefined ? document.documentElement.lang : '').toString().toLowerCase();
    if (htmlLang === 'he' || htmlLang === 'ar' || htmlLang.startsWith('he') === true || htmlLang.startsWith('ar') === true) {
      return true;
    }
    const navLang = (navigator?.language !== undefined ? navigator.language : '').toString().toLowerCase();
    if (navLang === 'he' || navLang === 'ar' || navLang.startsWith('he') === true || navLang.startsWith('ar') === true) {
      return true;
    }
  } catch (error) {
  }
  return false;
});
const html = ref('');
function updatePixels(){
  if (shellRef.value === null || shellRef.value === undefined) {
    shellSize.value = { width: 0, height: 0 };
    pixelStates.value = [];
    return;
  }
  const width = shellRef.value.clientWidth;
  const height = shellRef.value.clientHeight;
  shellSize.value = { width, height };
  const cols = Math.ceil(width / 40);
  const rows = Math.ceil(height / 40);
  const total = cols * rows;
  if (pixelStates.value.length !== total) {
    pixelStates.value = Array(total).fill(0);
  }
}
function getPixelClass(pixelTone) {
  return pixelToneClasses[pixelTone] || pixelToneClasses[0];
}
function getPreferredLanguageCode(){
  try {
    const fromI18n = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
    if (fromI18n === 'he' || fromI18n === 'en' || fromI18n === 'es' || fromI18n === 'it' || fromI18n === 'ar') {
      return fromI18n;
    }
    if (typeof fromI18n === 'string' && fromI18n.length >= 2) {
      const short = fromI18n.substring(0, 2);
      if (short === 'he' || short === 'en' || short === 'es' || short === 'it' || short === 'ar') {
        return short;
      }
    }
    const fromGlobal = (globalState?.orgLanguage !== undefined ? globalState.orgLanguage : '').toString().toLowerCase();
    if (fromGlobal === 'he' || fromGlobal === 'en' || fromGlobal === 'es' || fromGlobal === 'it' || fromGlobal === 'ar') {
      return fromGlobal;
    }
    if (typeof fromGlobal === 'string' && fromGlobal.length >= 2) {
      const short2 = fromGlobal.substring(0, 2);
      if (short2 === 'he' || short2 === 'en' || short2 === 'es' || short2 === 'it' || short2 === 'ar') {
        return short2;
      }
    }
    const htmlLang = (document?.documentElement?.lang !== undefined ? document.documentElement.lang : '').toString().toLowerCase();
    if (htmlLang === 'he' || htmlLang === 'en' || htmlLang === 'es' || htmlLang === 'it' || htmlLang === 'ar') {
      return htmlLang;
    }
    if (typeof htmlLang === 'string' && htmlLang.length >= 2) {
      const short3 = htmlLang.substring(0, 2);
      if (short3 === 'he' || short3 === 'en' || short3 === 'es' || short3 === 'it' || short3 === 'ar') {
        return short3;
      }
    }
    const navLang = (navigator?.language !== undefined ? navigator.language : '').toString().toLowerCase();
    if (navLang === 'he' || navLang === 'en' || navLang === 'es' || navLang === 'it' || navLang === 'ar') {
      return navLang;
    }
    if (typeof navLang === 'string' && navLang.length >= 2) {
      const short4 = navLang.substring(0, 2);
      if (short4 === 'he' || short4 === 'en' || short4 === 'es' || short4 === 'it' || short4 === 'ar') {
        return short4;
      }
    }
  } catch (error) {
  }
  return 'en';
}
function getPreferredLanguageShort(){
  const full = getPreferredLanguageCode();
  if (typeof full === 'string' && full.length >= 2) { return full.substring(0,2); }
  return 'en';
}
async function loadPolicy(){
  const lang = getPreferredLanguageCode();
  const candidates = [`/policies/terms.${lang}.md`,`/policies/terms.${lang.substring(0,2)}.md`,`/policies/terms.en.md`];
  let content = '';
  for (let i = 0; i < candidates.length; i = i + 1) {
    const url = candidates[i];
    try {
      const res = await fetch(url);
      if (res?.ok === true) {
        const txt = await res.text();
        content = txt;
        break;
      }
    } catch (e) {
    }
  }
  if (content.length > 0) {
    html.value = sanitizeHtml(marked.parse(content));
  } else {
    html.value = '';
  }
}
async function accept(){
  try {
    const response = await axios.post(`${globalState.apiEndpoint}/api/users/acceptTerms`, { version });
    const data = response !== undefined && response !== null && response.data !== undefined && response.data !== null ? response.data : {};
    emit('accepted', {
      version: data.version !== undefined && data.version !== null ? data.version : version,
      acceptedAt: data.acceptedAt !== undefined && data.acceptedAt !== null ? data.acceptedAt : new Date().toISOString(),
      accepted: data.accepted === true
    });
  } catch(error) {
    toast.error('לא הצלחנו לשמור את אישור תנאי השימוש. נסו שוב.');
  }
}
function logout(){
  emit('logout');
}
const renderer = new marked.Renderer();
marked.setOptions({ renderer, gfm: true, breaks: true });

onMounted(() => {
  if (document !== undefined && document.documentElement !== undefined) {
    document.documentElement.style.overflow = 'hidden';
  }
  if (document !== undefined && document.body !== undefined) {
    document.body.style.overflow = 'hidden';
  }
  updatePixels();
  if (typeof ResizeObserver === 'function') {
    shellObserver = new ResizeObserver(() => {
      updatePixels();
    });
    if (shellRef.value !== null && shellRef.value !== undefined) {
      shellObserver.observe(shellRef.value);
    }
  } else {
    window.addEventListener('resize', updatePixels);
  }
  pixelInterval = setInterval(() => {
    const availableIndices = [];
    for (let i = 0; i < pixelStates.value.length; i = i + 1) {
      if (pixelStates.value[i] === 0) {
        availableIndices.push(i);
      }
    }
    if (availableIndices.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableIndices.length);
      const nextShade = Math.floor(Math.random() * (pixelToneClasses.length - 1)) + 1;
      pixelStates.value[availableIndices[randomIndex]] = nextShade;
    } else {
      const resetCount = Math.max(1, Math.floor(pixelStates.value.length * 0.08));
      let count = 0;
      while (count < resetCount) {
        const index = Math.floor(Math.random() * pixelStates.value.length);
        pixelStates.value[index] = 0;
        count = count + 1;
      }
    }
  }, 65);
  loadPolicy();
})
onUnmounted(() => {
  if (document !== undefined && document.documentElement !== undefined) {
    document.documentElement.style.overflow = '';
  }
  if (document !== undefined && document.body !== undefined) {
    document.body.style.overflow = '';
  }
  if (pixelInterval !== null) {
    clearInterval(pixelInterval);
  }
  if (shellObserver !== null) {
    shellObserver.disconnect();
  } else {
    window.removeEventListener('resize', updatePixels);
  }
})
</script>
<style scoped>
.prose {
  line-height: 1.6;
}
.terms-modal-shell {
  background-image: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%);
}
.terms-modal-scroll {
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgb(148 163 184) rgb(226 232 240);
}
.terms-modal-scroll::-webkit-scrollbar {
  width: 12px;
}
.terms-modal-scroll::-webkit-scrollbar-track {
  background: rgb(226 232 240);
  border-radius: 999px;
}
.terms-modal-scroll::-webkit-scrollbar-thumb {
  background: rgb(148 163 184);
  border-radius: 999px;
  border: 2px solid rgb(226 232 240);
}
:global(.dark) .terms-modal-scroll {
  scrollbar-color: rgb(100 116 139) rgb(30 41 59);
}
:global(.dark) .terms-modal-scroll::-webkit-scrollbar-track {
  background: rgb(30 41 59);
}
:global(.dark) .terms-modal-scroll::-webkit-scrollbar-thumb {
  background: rgb(100 116 139);
  border-color: rgb(30 41 59);
}
.policy-content :deep(h1) {
  margin-top: 0;
  margin-bottom: 1.25rem;
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.policy-content :deep(h2) {
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  font-size: 1.05rem;
  line-height: 1.6rem;
  font-weight: 700;
  color: rgb(15 23 42);
}
.policy-content :deep(p) {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  color: rgb(51 65 85);
}
.policy-content :deep(ul) {
  margin-top: 0.9rem;
  margin-bottom: 0.9rem;
  padding-inline-start: 1.25rem;
}
.policy-content :deep(li) {
  margin-top: 0.4rem;
  margin-bottom: 0.4rem;
  color: rgb(51 65 85);
}
.policy-content :deep(strong) {
  color: rgb(15 23 42);
}
:global(.dark) .terms-modal-shell {
  background-image: linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%);
}
:global(.dark) .policy-content :deep(h1),
:global(.dark) .policy-content :deep(h2),
:global(.dark) .policy-content :deep(strong) {
  color: rgb(241 245 249);
}
:global(.dark) .policy-content :deep(p),
:global(.dark) .policy-content :deep(li) {
  color: rgb(203 213 225);
}
</style>